import Input from './Input';
import { kebabCase } from 'lodash';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { TextAlign } from 'venn-ui-kit';

export interface TitledDollarInputProps {
  title: string;
  subtitle?: string;
  value: number;
  disabled?: boolean;
  setValue: (value: number) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  testId?: string;
  textAlign?: TextAlign;
  min?: number;
  max?: number;
}

export const formattedDollarValue = (str: string) => {
  const [integer = '', decimal] = str.split('.');
  const formatted = integer === '' ? '' : Number(integer).toLocaleString('en-US');
  const formattedDecimal = decimal !== undefined ? `${formatted}.${decimal}` : formatted;
  return `$${formattedDecimal}`;
};

const clamp = (num: number, min?: number, max?: number) => {
  let clamped = num;
  if (min !== undefined) clamped = Math.max(min, clamped);
  if (max !== undefined) clamped = Math.min(max, clamped);
  return clamped;
};

export const TitledDollarInput = ({
  title,
  value,
  disabled,
  setValue,
  subtitle,
  onFocus,
  onBlur,
  testId,
  textAlign = TextAlign.Right,
  min,
  max,
}: TitledDollarInputProps) => {
  const [inputStr, setInputStr] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);
  const cursorRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (cursorRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(cursorRef.current, cursorRef.current);
      cursorRef.current = null;
    }
  });

  return (
    <div>
      <div className="text-[12px] font-bold mr-[5px]">
        {title}
        {subtitle && <span className="font-normal"> {subtitle}</span>}
      </div>
      <Input
        className="mt-0.5 w-24"
        textAlign={textAlign}
        disabled={disabled}
        setInputRef={inputRef}
        data-testid={testId ?? `qa-${kebabCase(title)}-tax-rate-defaults-input`}
        value={formattedDollarValue(inputStr)}
        onChange={(str) => {
          const prevLength = str.length;
          const cursorPos = inputRef.current?.selectionStart ?? null;
          const stripped = str.replace(/[^0-9.]/g, '').replace(/(\.\d{0,2})\d*/, '$1');
          const parsed = parseFloat(stripped);
          if (!Number.isNaN(parsed)) {
            const clamped = clamp(parsed, min, max);
            setValue(clamped);
            const newStr = clamped !== parsed ? String(clamped) : stripped;
            setInputStr(newStr);
            if (cursorPos !== null) {
              const newLength = formattedDollarValue(newStr).length;
              cursorRef.current = cursorPos + (newLength - prevLength);
            }
          } else if (stripped.length === 0) {
            setInputStr(stripped);
            cursorRef.current = 1;
          }
        }}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    </div>
  );
};
